<template>
  <v-text-field
    v-model.number="model"
    v-bind="$attrs"
    class="centered-input t-number-input"
    type="number"
    variant="outlined"
    :max="max"
    :min="min"
    @blur="model = getSafeValue(model)"
  >
    <template #append-inner>
      <v-icon
        :style="modelValue >= max ? 'cursor: auto' : 'cursor: pointer'"
        :color="modelValue >= max ? 'transparent' : 'dark-grey'"
        @click="add"
      >
        {{ appendIcon }}
      </v-icon>
    </template>
    <template #prepend-inner>
      <v-icon style="cursor: pointer" color="dark-grey" @click="subtract">
        {{ prependInnerIcon }}
      </v-icon>
    </template>
  </v-text-field>
</template>

<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
  name: "TNumberInput",

  props: {
    appendIcon: {
      type: String,
      default: "mdi-plus",
    },
    max: {
      type: [Number, String],
      default: undefined,
    },
    min: {
      type: [Number, String],
      default: "0",
    },
    prependInnerIcon: {
      type: String,
      default: "mdi-minus",
    },
    unit: {
      type: [Number, String],
      default: 1,
    },
    modelValue: {
      type: [Number, String],
      default: 0,
    },
  },

  emits: ["decremented", "update:modelValue", "incremented"],

  computed: {
    model: {
      get(): number | string {
        return this.getSafeValue(this.modelValue);
      },
      set(value: number) {
        this.$emit("update:modelValue", this.getSafeValue(value));
      },
    },
  },

  methods: {
    add() {
      const unit = Number(this.unit);
      const model = Number(this.model);

      if (!this.hasMaxValue()) {
        this.model = model + unit;
        return;
      }

      const max = Number(this.max);

      if (unit + model > max) return;

      this.model = model + unit;
      this.$emit("incremented");
    },
    hasMaxValue(): boolean {
      return this.max !== null && this.max !== undefined;
    },
    hasMinValue(): boolean {
      return this.min !== null && this.min !== undefined;
    },
    subtract() {
      const unit = Number(this.unit);
      const model = Number(this.model);

      if (!this.hasMinValue()) {
        this.model = model + unit;
        return;
      }

      const min = Number(this.min);

      if (model - unit < min) return;

      this.model = model - unit;
      this.$emit("decremented");
    },
    getSafeValue(value: number | string) {
      const parsedValue = Number(value);

      if (Number.isNaN(parsedValue)) return 0;

      if (this.hasMaxValue() && parsedValue > Number(this.max)) return Number(this.max);

      if (this.hasMinValue() && parsedValue < Number(this.min)) return Number(this.min);

      return value;
    },
  },
});
</script>

<style scoped>
.centered-input :deep(input) {
  text-align: center;
}

.centered-input :deep(input::-webkit-outer-spin-button),
.centered-input :deep(input::-webkit-inner-spin-button) {
  -webkit-appearance: none;
  margin: 0;
}

.centered-input :deep(input[type="number"]) {
  -moz-appearance: textfield;
  appearance: textfield;
}
</style>
